import { jsx, jsxs } from 'react/jsx-runtime';
import { AudioContextProvider, useAudioContext } from '../../../../mediaPlaying/providers/AudioProvider.js';
import { IconVoiceNoteAudioPause, IconVoiceNoteAudioPlay } from '@fabric-app/icons/svg';
import { Flex } from '@fabric-app/ui/components/Flex';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { formatDuration } from '@fabric-app/utils/number';
import * as Slider from '@radix-ui/react-slider';
import { useState } from 'react';
import { styled } from 'styled-components';

const AudioElement = styled.audio.withConfig({
  displayName: "PreviewAudioPlayer__AudioElement",
  componentId: "sc-752ce1e3-0"
})([
  `display:none;`
]);
const TimeSliderRoot = styled(Slider.Root).withConfig({
  displayName: "PreviewAudioPlayer__TimeSliderRoot",
  componentId: "sc-752ce1e3-1"
})([
  `display:inline-flex;align-items:center;width:6.25rem;height:1.875rem;position:relative;outline:none;pointer-events:auto;cursor:pointer;user-select:none;touch-action:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent;`
]);
const TimeSliderTrack = styled(Slider.Track).withConfig({
  displayName: "PreviewAudioPlayer__TimeSliderTrack",
  componentId: "sc-752ce1e3-2"
})([
  `position:relative;width:100%;height:0.125rem;border-radius:0.188rem;background:`,
  `;`
], cssVar["color-bg-senary"]);
const TimeSliderTrackProgress = styled(Slider.Range).withConfig({
  displayName: "PreviewAudioPlayer__TimeSliderTrackProgress",
  componentId: "sc-752ce1e3-3"
})([
  `background:`,
  `;position:absolute;border-radius:0.188rem;height:100%;will-change:left right;`
], cssVar["color-bg-octonary"]);
const TimeSliderThumb = styled(Slider.Thumb).withConfig({
  displayName: "PreviewAudioPlayer__TimeSliderThumb",
  componentId: "sc-752ce1e3-4"
})([
  `display:block;width:0.75rem;height:0.75rem;border-radius:50%;background:`,
  `;transition:opacity 0.1s ease-in-out;&[data-disabled]{opacity:0;pointer-events:none;}`,
  `{&:hover{background:`,
  `;}}`
], cssVar["color-bg-octonary"], mediaHover, cssVar["color-text-primary"]);
const StyledTime = styled.div.withConfig({
  displayName: "PreviewAudioPlayer__StyledTime",
  componentId: "sc-752ce1e3-5"
})([
  `color:`,
  `;font-family:Inter,sans-serif;font-size:12px;font-style:normal;font-weight:500;line-height:normal;min-width:2.188rem;`
], cssVar["color-text-tertiary"]);
const TimeDisplay = () => {
  const { duration, currentTime, isPlaying } = useAudioContext();
  return /* @__PURE__ */ jsx(StyledTime, {
    children: !isPlaying ? formatDuration(duration) : formatDuration(currentTime)
  });
};
const StyledMediaPlayer = styled(Flex).attrs((p) => {
  return {
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "PreviewAudioPlayer__StyledMediaPlayer",
  componentId: "sc-752ce1e3-6"
})([
  `padding:0 0.375rem;gap:0.375rem;z-index:5;user-select:none !important;*{user-select:none !important;}pointer-events:auto;touch-action:none;`
]);
const StyledPlayButton = styled.button.withConfig({
  displayName: "PreviewAudioPlayer__StyledPlayButton",
  componentId: "sc-752ce1e3-7"
})([
  `display:flex;width:20px;height:20px;justify-content:center;align-items:center;gap:10px;border-radius:3px;color:`,
  `;`,
  `{&:hover{color:`,
  `;background:`,
  `;}}`
], cssVar["color-text-tertiary"], mediaHover, cssVar["color-text-primary"], cssVar["color-bg-quinary"]);
const Play = () => {
  const { isPlaying, togglePlay } = useAudioContext();
  return /* @__PURE__ */ jsx(Tooltip, {
    label: isPlaying ? "Pause" : "Play",
    children: /* @__PURE__ */ jsx(StyledPlayButton, {
      onClick: togglePlay,
      children: isPlaying ? /* @__PURE__ */ jsx(IconVoiceNoteAudioPause, {}) : /* @__PURE__ */ jsx(IconVoiceNoteAudioPlay, {})
    })
  });
};
const TimeSlider = () => {
  const { currentTime, duration, scrub, isReady } = useAudioContext();
  const value = currentTime === 0 || duration === 0 ? [
    0
  ] : [
    currentTime / duration * 100
  ];
  const onValueChange = (values) => {
    scrub(values[0] / 100 * duration);
  };
  return /* @__PURE__ */ jsxs(TimeSliderRoot, {
    defaultValue: [
      0
    ],
    value,
    onValueChange,
    min: 0,
    max: 100,
    disabled: !isReady,
    children: [
      /* @__PURE__ */ jsx(TimeSliderTrack, {
        children: /* @__PURE__ */ jsx(TimeSliderTrackProgress, {})
      }),
      /* @__PURE__ */ jsx(TimeSliderThumb, {})
    ]
  });
};
const prevent = (e) => {
  e.preventDefault();
  e.stopPropagation();
};
const stopPropagation = (e) => {
  e.stopPropagation();
};
const PreviewAudioPlayer = ({ fileUrl }) => {
  const [audioElement, setAudioElement] = useState(null);
  return /* @__PURE__ */ jsx(AudioContextProvider, {
    audioElement,
    children: /* @__PURE__ */ jsxs(StyledMediaPlayer, {
      draggable: false,
      onDragStart: prevent,
      onDragOver: prevent,
      onDrop: prevent,
      onDragEnter: prevent,
      onDragLeave: prevent,
      onPointerDown: stopPropagation,
      children: [
        /* @__PURE__ */ jsx(AudioElement, {
          ref: setAudioElement,
          src: fileUrl,
          controls: false,
          preload: "metadata"
        }),
        /* @__PURE__ */ jsx(Play, {}),
        /* @__PURE__ */ jsx(TimeSlider, {}),
        /* @__PURE__ */ jsx(TimeDisplay, {})
      ]
    })
  });
};

export { PreviewAudioPlayer };
//# sourceMappingURL=PreviewAudioPlayer.js.map
